/**
 * Service am Auftrag – ein eigener Vorgang, parallel zur Kette.
 *
 * Unter den Bestellungen, an einer orangefarbenen Linie statt der dunklen:
 * Die Farbe sagt „parallel zum Prozess, nicht Teil der Kette". Ein
 * Servicefall ist Kopf plus Servicepunkte; jeder Punkt hat Maßnahme,
 * Verantwortlichen und Termin oder Zeitfenster. Abgeschlossen wird erst, wenn
 * der letzte Haken gesetzt ist – der grüne Knopf wird es dann von selbst.
 *
 * Rot gibt es hier nur, wenn ein Punkt offen ist: der Haken-Knopf des
 * nächsten offenen Punkts. Nach der Abnahme ist das der einzige rote Knopf
 * auf der Maske (Richtlinie 19).
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import { IconHaken, IconStift, IconWerkzeug } from './Icons';
import { ServicefallModal } from '../modals/ServicefallModal';
import { ServicepunktModal } from '../modals/ServicepunktModal';
import { ServiceAbschlussModal } from '../modals/ServiceAbschlussModal';
import type { Angebot, AngebotAntwort, Servicefall, Servicepunkt } from '../typen';
import './serviceblock.css';

export function Serviceblock({ angebot, onFertig }: { angebot: Angebot; onFertig: () => void }) {
  const { toastZeigen } = useKora();
  const [neuOffen, setNeuOffen] = useState(false);
  const [punktFuer, setPunktFuer] = useState<{ fall: Servicefall; punkt: Servicepunkt | null } | null>(null);
  const [abschlussFuer, setAbschlussFuer] = useState<Servicefall | null>(null);

  const faelle = angebot.servicefaelle;
  const offen = angebot.serviceOffen;

  const antwort = (a: AngebotAntwort) => {
    onFertig();
    if (a.toast) toastZeigen(a.toast);
  };
  const fehlerZeigen = (f: unknown) => toastZeigen(f instanceof Error ? f.message : 'Das hat nicht geklappt.');

  const haken = useMutation({
    mutationFn: ({ id, erledigt }: { id: number; erledigt: boolean }) =>
      api.aendere<AngebotAntwort>(`/service/punkte/${id}`, { erledigt }),
    onSuccess: antwort,
    onError: fehlerZeigen,
  });

  const wiederOeffnen = useMutation({
    mutationFn: (id: number) => api.sende<AngebotAntwort>(`/service/${id}/wieder-oeffnen`),
    onSuccess: antwort,
    onError: fehlerZeigen,
  });

  const schlagzeile =
    faelle.length === 0
      ? 'Kein Servicefall'
      : faelle.length === 1
        ? '1 Servicefall'
        : `${faelle.length} Servicefälle`;
  const zusatz = !angebot.serviceMoeglich
    ? 'möglich ab „Montiert"'
    : faelle.length === 0
      ? angebot.status === 'Abgenommen'
        ? 'Gewährleistung, Kulanz oder Reparatur – hier eröffnen'
        : 'Mängel vor der Abnahme – hier eröffnen'
      : offen > 0
        ? `${offen} ${offen === 1 ? 'Punkt' : 'Punkte'} offen`
        : 'alle Punkte erledigt';

  return (
    <div className="sv" id="service">
      <div className="sv-kopf">
        <div className="sv-kopf-links">
          <span className="sv-symbol" aria-hidden="true">
            <IconWerkzeug groesse={16} />
          </span>
          <div>
            <div className="ab-kicker">Service</div>
            <div className="ab-schlagzeile">
              {schlagzeile}
              <span className="ab-schlagzeile-zusatz"> · {zusatz}</span>
            </div>
          </div>
        </div>
        <button
          type="button"
          className="ab-knopf"
          disabled={!angebot.serviceMoeglich}
          title={
            angebot.serviceMoeglich
              ? 'Einen Servicefall eröffnen – Mangel, Gewährleistung, Kulanz oder Reparatur'
              : 'Ein Servicefall ist erst ab „Montiert" möglich – davor ist es Abwicklung'
          }
          onClick={() => setNeuOffen(true)}
        >
          + Servicefall
        </button>
      </div>

      {faelle.length > 0 && (
        <div className="sv-zweig">
          {faelle.map((f) => {
            const naechsterOffen = f.punkte.find((p) => !p.erledigt);
            return (
              <article key={f.id} className={`sv-karte ${f.abgeschlossen ? 'abgeschlossen' : ''}`}>
                <header className="sv-karte-kopf">
                  <span className="sv-quadrat">{f.kennung}</span>
                  <div className="sv-karte-titel">
                    <div className="sv-titel">{f.titel}</div>
                    <div className="sv-meta">
                      {[
                        `gemeldet ${f.gemeldetText}`,
                        f.kanal,
                        f.bezugText,
                        angebot.gewaehrleistungText ? `Gewährleistung bis ${angebot.gewaehrleistungText}` : '',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  <span className={`ab-pille ${f.abgeschlossen ? 'fertig' : ''}`}>
                    <span className="ab-pille-punkt" />
                    <b>
                      {f.abgeschlossen
                        ? `Abgeschlossen · ${f.ergebnisLabel}`
                        : `${f.offen} von ${f.gesamt} ${f.gesamt === 1 ? 'Punkt' : 'Punkten'} offen`}
                    </b>
                    {f.abgeschlossen && <span className="ab-pille-seit">{f.abgeschlossenText}</span>}
                  </span>
                </header>

                <ul className="sv-punkte">
                  {f.punkte.map((p) => {
                    const naechster = !f.abgeschlossen && naechsterOffen?.id === p.id;
                    return (
                      <li key={p.id} className={`sv-punkt ${p.erledigt ? 'erledigt' : ''} ${p.ueberfaellig ? 'ueberfaellig' : ''}`}>
                        <button
                          type="button"
                          className={`sv-haken ${p.erledigt ? 'an' : ''} ${naechster ? 'naechster' : ''}`}
                          disabled={f.abgeschlossen || haken.isPending}
                          title={
                            p.erledigt
                              ? `Erledigt ${p.erledigtText} von ${p.erledigtVon} – Klick nimmt den Haken zurück`
                              : 'Als erledigt abhaken'
                          }
                          onClick={() => haken.mutate({ id: p.id, erledigt: !p.erledigt })}
                        >
                          {p.erledigt && <IconHaken groesse={13} strich={2.4} />}
                        </button>
                        <div className="sv-punkt-text">
                          <div className="sv-massnahme">{p.massnahme}</div>
                          {(p.bezug || p.notiz) && (
                            <div className="sv-punkt-unter">{[p.bezug, p.notiz].filter(Boolean).join(' · ')}</div>
                          )}
                        </div>
                        <span className={`sv-termin ${p.erledigt ? 'erledigt' : p.ueberfaellig ? 'ueber' : ''}`}>
                          {p.terminText}
                          {!p.erledigt && !p.termin && p.zeitfenster ? <span className="sv-termin-art"> · Zeitfenster</span> : null}
                        </span>
                        <span className="sv-person" title={`${p.verantwortlichName} · verantwortlich`}>
                          {p.verantwortlichInitialen}
                        </span>
                        {!f.abgeschlossen && (
                          <button
                            type="button"
                            className="ab-stift sv-stift"
                            title="Punkt bearbeiten"
                            onClick={() => setPunktFuer({ fall: f, punkt: p })}
                          >
                            <IconStift groesse={14} />
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>

                <footer className="sv-fuss">
                  {f.abgeschlossen ? (
                    <>
                      <span className="sv-fuss-text">
                        Abgeschlossen {f.abgeschlossenText} von {f.abgeschlossenVon} · {f.ergebnisLabel}
                        {f.notiz ? ` · ${f.notiz}` : ''}
                      </span>
                      <button
                        type="button"
                        className="ab-textlink sv-leise"
                        disabled={wiederOeffnen.isPending}
                        onClick={() => wiederOeffnen.mutate(f.id)}
                      >
                        Verklickt? Wieder öffnen
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="ab-knopf" onClick={() => setPunktFuer({ fall: f, punkt: null })}>
                        + Punkt
                      </button>
                      <span className="ab-luft" />
                      <button
                        type="button"
                        className="sv-abschluss"
                        disabled={f.offen > 0}
                        title={
                          f.offen > 0
                            ? `Noch ${f.offen} ${f.offen === 1 ? 'Punkt' : 'Punkte'} offen – abgeschlossen wird, wenn der letzte Haken gesetzt ist`
                            : 'Alle Punkte erledigt – Ergebnis wählen und abschließen'
                        }
                        onClick={() => setAbschlussFuer(f)}
                      >
                        Servicefall abschließen
                      </button>
                    </>
                  )}
                </footer>
              </article>
            );
          })}
        </div>
      )}

      {neuOffen && (
        <ServicefallModal angebot={angebot} onSchliessen={() => setNeuOffen(false)} onFertig={onFertig} />
      )}
      {punktFuer && (
        <ServicepunktModal
          fall={punktFuer.fall}
          punkt={punktFuer.punkt}
          angebot={angebot}
          onSchliessen={() => setPunktFuer(null)}
          onFertig={onFertig}
        />
      )}
      {abschlussFuer && (
        <ServiceAbschlussModal fall={abschlussFuer} onSchliessen={() => setAbschlussFuer(null)} onFertig={onFertig} />
      )}
    </div>
  );
}
